import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Button, EmptyState, Field, Skeleton, TableWrap } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action, ApiError, saveBlob, upload } from '../../lib/api.ts';
import { dateTime, day } from '../../lib/format.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction } from './common.tsx';

type Kind = 'text' | 'number' | 'int' | 'money' | 'cost' | 'percent' | 'date' | 'bool' | 'choice';
interface ImportField {
  key: string;
  label: string;
  kind: Kind;
  required?: boolean;
  hint?: string;
  choices?: { value: string }[];
}
interface Target {
  key: TargetKey;
  label: string;
  match: string;
  fields: ImportField[];
}
interface HistoryRow {
  id: string;
  target: TargetKey;
  file_name: string;
  sheet: string | null;
  rows: number;
  created: number;
  updated: number;
  at: string;
  by_name: string | null;
}
interface Overview {
  targets: Target[];
  history: HistoryRow[];
  today: string;
  max_rows: number;
}
interface SheetInfo {
  name: string;
  header_row: number;
  columns: string[];
  rows: number;
  sample: string[][];
  mapping: Record<string, number | null>;
}
interface Problem {
  row: number;
  column?: string;
  message: string;
}
interface Result {
  dry: boolean;
  done: boolean;
  rows: number;
  created: number;
  updated: number;
  errors: Problem[];
  error_count: number;
}

const TARGET_KEYS = ['customers', 'suppliers', 'products', 'stock', 'accounts', 'opening', 'prospects'] as const;
type TargetKey = (typeof TARGET_KEYS)[number];
const ICONS: Record<TargetKey, IconName> = {
  customers: 'users',
  suppliers: 'truck',
  products: 'receipt',
  stock: 'box',
  accounts: 'book',
  opening: 'bank',
  prospects: 'contact',
};
/** Where the imported records are seen afterwards. */
const PLACES: Record<TargetKey, string> = {
  customers: '/guests',
  suppliers: '/manage/purchasing',
  products: '/manage/menu',
  stock: '/manage/stock',
  accounts: '/accounting',
  opening: '/accounting',
  prospects: '/outreach',
};

const FIELD_KEYS = [
  'name',
  'phone',
  'email',
  'is_company',
  'tax_id',
  'address',
  'town',
  'country',
  'payment_days',
  'credit_limit',
  'birthday',
  'note',
  'price',
  'category',
  'tax_rate',
  'taxable',
  'sku',
  'barcode',
  'plu',
  'description',
  'active',
  'unit',
  'quantity',
  'unit_cost',
  'min_qty',
  'supplier',
  'location',
  'code',
  'type',
  'account',
  'debit',
  'credit',
  'balance',
  'label',
  'partner',
  'company',
  'website',
  'industry',
  'city',
  'company_phone',
  'fleet_size',
  'employees',
  'tags',
  'first_name',
  'last_name',
  'full_name',
  'job_title',
  'linkedin',
  'basis',
] as const;
const HINT_KEYS = ['is_company', 'country', 'price', 'price_added', 'category', 'tax_rate', 'taxable', 'quantity', 'location', 'balance', 'unit', 'type', 'tags', 'basis'] as const;

const fieldLabel = (target: TargetKey, f: ImportField) => {
  if (target === 'opening' && f.key === 'account') return t('imp.field_account_code');
  return (FIELD_KEYS as readonly string[]).includes(f.key) ? t(`imp.field_${f.key as (typeof FIELD_KEYS)[number]}`) : f.label;
};
const fieldHint = (f: ImportField, salesTax: boolean): string | undefined => {
  if (f.key === 'unit' || f.key === 'type')
    return t(`imp.hint_${f.key}`, {
      list: (f.choices ?? []).map((c) => c.value).join(', '),
    });
  const key = f.key === 'price' && salesTax ? 'price_added' : f.key;
  return (HINT_KEYS as readonly string[]).includes(key) && f.hint ? t(`imp.hint_${key as (typeof HINT_KEYS)[number]}`) : undefined;
};

/** The file as base64, which is how an upload travels in the request. */
function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const MAX_BYTES = 8_500_000;
const ACCEPT = '.xlsx,.xlsm,.csv,.tsv,.txt';

/**
 * Manage → Import data: customers, suppliers, products, stock, the chart of accounts and opening
 * balances from Excel or CSV. Four steps on one page: what, the file, its columns, the check.
 * Nothing is saved until every row passes, and the check says which row is wrong and why.
 */
export function DataImport() {
  const { data, reload } = useAction<Overview>('data_import.targets');
  const { fail, toast } = useUi();
  const [target, setTarget] = useState<TargetKey | null>(null);
  const [file, setFile] = useState<{
    name: string;
    size: number;
    data: string;
  } | null>(null);
  const [sheets, setSheets] = useState<SheetInfo[] | null>(null);
  const [sheet, setSheet] = useState(0);
  const [mapping, setMapping] = useState<Record<string, number | null>>({});
  const [date, setDate] = useState('');
  const [memo, setMemo] = useState('');
  const [reading, setReading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const chosen = data?.targets.find((x) => x.key === target) ?? null;
  const info = sheets?.[sheet] ?? null;
  const salesTax = Boolean(chosen?.fields.some((f) => f.key === 'taxable'));
  const missing = chosen?.fields.filter((f) => f.required && (mapping[f.key] === null || mapping[f.key] === undefined)) ?? [];

  const choose = (key: TargetKey) => {
    setTarget(key);
    setFile(null);
    setSheets(null);
    setResult(null);
    if (key === 'opening' && data) {
      setDate(data.today);
      setMemo(t('imp.opening_memo'));
    }
  };

  // Opened from another screen for one kind of data (Sales outreach → Import): that kind is chosen.
  const [params] = useSearchParams();
  const asked = params.get('target');
  useEffect(() => {
    if (!target && asked && data?.targets.some((x) => x.key === asked)) choose(asked as TargetKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, asked]);

  const take = async (f: File | undefined) => {
    if (!f || !target) return;
    setResult(null);
    if (f.size > MAX_BYTES) {
      fail(new ApiError(400, 'too_large', t('imp.too_large')));
      return;
    }
    setReading(true);
    try {
      const data64 = await readFile(f);
      const r = await upload<{ sheets: SheetInfo[] }>('data_import.read', {
        target,
        file: data64,
        name: f.name,
      });
      const first = Math.max(
        0,
        r.sheets.findIndex((s) => s.rows > 0),
      );
      setFile({ name: f.name, size: f.size, data: data64 });
      setSheets(r.sheets);
      setSheet(first);
      setMapping(r.sheets[first]?.mapping ?? {});
    } catch (err) {
      fail(err);
    } finally {
      setReading(false);
      if (input.current) input.current.value = '';
    }
  };

  const pickSheet = (i: number) => {
    setSheet(i);
    setMapping(sheets?.[i]?.mapping ?? {});
    setResult(null);
  };

  const go = async (dry: boolean) => {
    if (!file || !target || !info) return;
    const set = dry ? setChecking : setImporting;
    set(true);
    try {
      const r = await upload<Result>('data_import.run', {
        target,
        file: file.data,
        name: file.name,
        sheet: info.name,
        mapping,
        dry,
        ...(target === 'opening' ? { date, memo } : {}),
      });
      setResult(r);
      if (r.done) {
        toast(t('imp.done_toast', { n: r.rows }));
        void reload();
      }
    } catch (err) {
      fail(err);
    } finally {
      set(false);
    }
  };

  const template = async (key: TargetKey) => {
    try {
      const r = await action<{ name: string; data: string }>('data_import.template', { target: key });
      const bytes = Uint8Array.from(atob(r.data), (c) => c.charCodeAt(0));
      await saveBlob(
        new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        r.name,
      );
    } catch (err) {
      fail(err);
    }
  };

  const problemsCsv = () => {
    if (!result || !chosen || !target) return;
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const head = [t('imp.row'), t('imp.column'), t('imp.problem')].map(esc).join(',');
    const lines = result.errors.map((p) => [p.row ? String(p.row) : '', p.column ? colName(p.column) : '', p.message].map(esc).join(','));
    void saveBlob(new Blob([`﻿${[head, ...lines].join('\r\n')}`], { type: 'text/csv' }), `problems-${file?.name.replace(/\.[^.]+$/, '') ?? 'import'}.csv`);
  };

  const colName = (key: string) => {
    const f = chosen?.fields.find((x) => x.key === key);
    const col = mapping[key];
    const label = f && target ? fieldLabel(target, f) : key;
    return col !== null && col !== undefined && info ? `${label} (${info.columns[col]})` : label;
  };

  const reset = () => {
    setFile(null);
    setSheets(null);
    setResult(null);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    void take(e.dataTransfer.files[0]);
  };

  const step = !target ? 1 : !file ? 2 : !result ? 3 : 4;

  return (
    <ManageLayout title={t('manage.import')} subtitle={t('imp.subtitle')}>
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : !data.targets.length ? (
        <EmptyState icon="upload" title={t('imp.nothing')} body={t('imp.nothing_body')} />
      ) : (
        <div className="imp">
          <ol className="imp-steps" aria-label={t('imp.steps')}>
            {(['imp.step_what', 'imp.step_file', 'imp.step_columns', 'imp.step_check'] as const).map((k, i) => (
              <li key={k} className={i + 1 < step ? 'done' : i + 1 === step ? 'current' : ''} aria-current={i + 1 === step ? 'step' : undefined}>
                <span className="imp-step-dot" aria-hidden="true">
                  {i + 1 < step ? <Icon name="check" size={14} /> : i + 1}
                </span>
                <span>{t(k)}</span>
              </li>
            ))}
          </ol>

          <section className="imp-section" aria-labelledby="imp-what">
            <h2 id="imp-what" className="section-title">
              {t('imp.what')}
            </h2>
            <div className="imp-targets" role="radiogroup" aria-label={t('imp.what')}>
              {data.targets
                .filter((x) => !file || x.key === target)
                .map((x) => (
                  <div key={x.key} className={`imp-target${target === x.key ? ' selected' : ''}`}>
                    <button type="button" role="radio" aria-checked={target === x.key} className="imp-target-main" onClick={() => choose(x.key)}>
                      <span className="imp-target-icon" aria-hidden="true">
                        <Icon name={ICONS[x.key]} size={22} />
                      </span>
                      <span className="imp-target-text">
                        <strong>{t(`imp.t_${x.key}`)}</strong>
                        <span className="muted small">{t(`imp.t_${x.key}_match`)}</span>
                      </span>
                      {target === x.key ? <Icon name="checkCircle" size={20} className="imp-target-tick" /> : null}
                    </button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="download"
                      onClick={() => void template(x.key)}
                      aria-label={t('imp.template_for', {
                        what: t(`imp.t_${x.key}`),
                      })}
                    >
                      {t('imp.template')}
                    </Button>
                  </div>
                ))}
            </div>
            {file ? (
              <Button
                variant="ghost"
                icon="back"
                className="imp-change"
                onClick={() => {
                  reset();
                  setTarget(null);
                }}
              >
                {t('imp.change_what')}
              </Button>
            ) : null}
          </section>

          {chosen ? (
            <section className="imp-section" aria-labelledby="imp-file">
              <h2 id="imp-file" className="section-title">
                {t('imp.file')}
              </h2>
              {file && sheets ? (
                <div className="imp-file">
                  <span className="imp-file-icon" aria-hidden="true">
                    <Icon name="file" size={22} />
                  </span>
                  <div className="imp-file-text">
                    <strong>{file.name}</strong>
                    <span className="muted small">
                      {(file.size / 1024).toFixed(file.size < 102_400 ? 1 : 0)} KB · {tn('imp.rows_in_sheet', info?.rows ?? 0)}
                    </span>
                  </div>
                  {sheets.length > 1 ? (
                    <Field label={t('imp.sheet')}>
                      <select className="input" value={sheet} onChange={(e) => pickSheet(Number(e.target.value))}>
                        {sheets.map((s, i) => (
                          <option key={s.name} value={i}>
                            {s.name} ({tn('imp.rows_n', s.rows)})
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : null}
                  <Button variant="ghost" icon="repeat" onClick={reset}>
                    {t('imp.other_file')}
                  </Button>
                </div>
              ) : (
                <div
                  className={`imp-drop${drag ? ' over' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDrag(true);
                  }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={onDrop}
                >
                  <Icon name="upload" size={28} />
                  <p>
                    <strong>{t('imp.drop')}</strong>
                  </p>
                  <p className="muted small">{t('imp.drop_hint')}</p>
                  <input ref={input} type="file" accept={ACCEPT} hidden onChange={(e) => void take(e.target.files?.[0])} />
                  <Button variant="primary" icon="file" busy={reading} onClick={() => input.current?.click()}>
                    {t('imp.choose_file')}
                  </Button>
                </div>
              )}
            </section>
          ) : null}

          {chosen && info && target ? (
            <section className="imp-section" aria-labelledby="imp-cols">
              <h2 id="imp-cols" className="section-title">
                {t('imp.columns')}
              </h2>
              <p className="muted">{t('imp.columns_lead', { row: info.header_row })}</p>
              {info.rows === 0 ? (
                <div className="callout callout-warn" role="status">
                  <Icon name="alert" size={18} />
                  <span>{t('imp.empty_sheet')}</span>
                </div>
              ) : null}
              <TableWrap>
                <table className="data-table imp-map">
                  <thead>
                    <tr>
                      <th>{t('imp.our_field')}</th>
                      <th className="left">{t('imp.your_column')}</th>
                      <th className="left imp-sample-col">{t('imp.first_values')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chosen.fields.map((f) => {
                      const col = mapping[f.key];
                      const values =
                        col === null || col === undefined
                          ? []
                          : info.sample
                              .map((r) => r[col] ?? '')
                              .filter(Boolean)
                              .slice(0, 3);
                      const hint = fieldHint(f, salesTax);
                      const id = `imp-col-${f.key}`;
                      return (
                        <tr key={f.key} className={f.required && (col === null || col === undefined) ? 'imp-missing' : ''}>
                          <td>
                            <label htmlFor={id} className="imp-field-name">
                              {fieldLabel(target, f)}
                              {f.required ? (
                                <span className="field-req" aria-hidden="true">
                                  {' '}
                                  *
                                </span>
                              ) : null}
                            </label>
                            {hint ? <span className="muted small imp-field-hint">{hint}</span> : null}
                          </td>
                          <td className="left">
                            <select
                              id={id}
                              className="input"
                              value={col ?? ''}
                              aria-required={f.required || undefined}
                              onChange={(e) => {
                                setMapping((m) => ({
                                  ...m,
                                  [f.key]: e.target.value === '' ? null : Number(e.target.value),
                                }));
                                setResult(null);
                              }}
                            >
                              <option value="">{f.required ? t('imp.choose_column') : t('imp.not_imported')}</option>
                              {info.columns.map((c, i) => (
                                <option key={i} value={i}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="left imp-sample-col">
                            {values.length ? (
                              <span className="imp-samples">
                                {values.map((v, i) => (
                                  <span key={i} className="imp-sample">
                                    {v}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>
              {target === 'opening' ? (
                <div className="form-grid imp-opening">
                  <Field label={t('imp.opening_date')} hint={t('imp.opening_date_hint')} required>
                    <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </Field>
                  <Field label={t('imp.opening_memo_label')} required>
                    <input className="input" value={memo} maxLength={200} onChange={(e) => setMemo(e.target.value)} />
                  </Field>
                </div>
              ) : null}
              <div className="imp-actions">
                {missing.length ? (
                  <span className="muted small" role="status">
                    <Icon name="info" size={14} />{' '}
                    {t('imp.choose_missing', {
                      fields: missing.map((f) => fieldLabel(target, f)).join(', '),
                    })}
                  </span>
                ) : (
                  <span className="muted small">{t('imp.match_rule', { rule: t(`imp.t_${target}_match`) })}</span>
                )}
                <Button
                  variant={result ? 'secondary' : 'primary'}
                  icon="shieldCheck"
                  busy={checking}
                  disabled={Boolean(missing.length) || !info.rows || (target === 'opening' && (!date || memo.trim().length < 3))}
                  onClick={() => void go(true)}
                >
                  {t('imp.check')}
                </Button>
              </div>
            </section>
          ) : null}

          {result && target ? (
            <section className="imp-section" aria-labelledby="imp-result" aria-live="polite">
              <h2 id="imp-result" className="section-title">
                {result.done ? t('imp.imported') : t('imp.check_title')}
              </h2>
              <div className="imp-stats">
                <Stat label={t('imp.rows')} value={result.rows} />
                <Stat label={target === 'opening' ? t('imp.lines') : t('imp.new')} value={result.created} tone="ok" />
                {target === 'opening' ? null : <Stat label={t('imp.updated')} value={result.updated} tone="info" />}
                <Stat label={t('imp.problems')} value={result.error_count} tone={result.error_count ? 'danger' : 'neutral'} />
              </div>
              {result.done ? (
                <div className="callout callout-ok" role="status">
                  <Icon name="checkCircle" size={18} />
                  <span>{t('imp.done_body', { n: result.rows })}</span>
                  <Link className="btn btn-secondary btn-sm" to={PLACES[target]}>
                    <span className="btn-label">{t('imp.open_records')}</span>
                  </Link>
                </div>
              ) : result.error_count ? (
                <>
                  <div className="callout callout-danger" role="alert">
                    <Icon name="alert" size={18} />
                    <span>{tn('imp.fix_rows', result.error_count)}</span>
                    <Button size="sm" icon="download" onClick={problemsCsv}>
                      {t('imp.download_problems')}
                    </Button>
                  </div>
                  <TableWrap className="imp-problems">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('imp.row')}</th>
                          <th className="left">{t('imp.column')}</th>
                          <th className="left">{t('imp.problem')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((p, i) => (
                          <tr key={i}>
                            <td className="tabular mono">{p.row || '—'}</td>
                            <td className="left">{p.column ? colName(p.column) : '—'}</td>
                            <td className="left">{p.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                  {result.error_count > result.errors.length ? (
                    <p className="muted small">
                      {t('imp.more_problems', {
                        n: result.error_count - result.errors.length,
                      })}
                    </p>
                  ) : null}
                </>
              ) : (
                <div className="imp-ready">
                  <div className="callout callout-ok" role="status">
                    <Icon name="checkCircle" size={18} />
                    <span>
                      {target === 'opening'
                        ? t('imp.ready_opening', { n: result.created })
                        : t('imp.ready', {
                            created: result.created,
                            updated: result.updated,
                          })}
                    </span>
                  </div>
                  <Button variant="primary" size="lg" icon="upload" busy={importing} onClick={() => void go(false)}>
                    {tn('imp.import_n', result.rows)}
                  </Button>
                </div>
              )}
            </section>
          ) : null}

          <section className="imp-section" aria-labelledby="imp-history">
            <h2 id="imp-history" className="section-title">
              {t('imp.history')}
            </h2>
            {!data.history.length ? (
              <p className="muted">{t('imp.history_empty')}</p>
            ) : (
              <TableWrap>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('imp.when')}</th>
                      <th className="left">{t('imp.what_col')}</th>
                      <th className="left imp-hide-phone">{t('imp.file_col')}</th>
                      <th>{t('imp.new')}</th>
                      <th>{t('imp.updated')}</th>
                      <th className="left imp-hide-phone">{t('imp.by')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map((h) => (
                      <tr key={h.id}>
                        <td title={dateTime(h.at)}>{day(h.at.slice(0, 10))}</td>
                        <td className="left">{(TARGET_KEYS as readonly string[]).includes(h.target) ? t(`imp.t_${h.target}`) : h.target}</td>
                        <td className="left imp-hide-phone imp-file-name">{h.file_name}</td>
                        <td className="tabular">{h.created}</td>
                        <td className="tabular">{h.updated}</td>
                        <td className="left imp-hide-phone">{h.by_name ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </section>
        </div>
      )}
    </ManageLayout>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'ok' | 'info' | 'danger' | 'neutral' }) {
  return (
    <div className={`imp-stat imp-stat-${tone}`}>
      <span className="imp-stat-value tabular">{value.toLocaleString()}</span>
      <span className="muted small">{label}</span>
    </div>
  );
}
